import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { ENDPOINTS } from '@/config/endpoints';
import HSNPOPUP from "./hsnPopup";
import { useRouter } from 'next/router';

const StepTwoForm = ({ formData, handleChange, errors, setFormData, setErrors }) => {
    const [containersOptions, setContainersOptions] = useState([]);
    const [containerInput, setContainerInput] = useState(formData?.containerTypeLabel || '');
    const [commodityInput, setCommodityInput] = useState(formData?.commodityLabel || '');
    const [commodityDropOptions, setCommodityDropOptions] = useState([]);
    const [showCommodityDropdown, setShowCommodityDropdown] = useState(false);
    const [showContainerDropdown, setShowContainerDropdown] = useState(false);
    const [openHSNPopup, setOpenHSNPopup] = useState(false);
    const [commodityLoading, setCommodityLoading] = useState(false);
    const [isSelectingCommodity, setIsSelectingCommodity] = useState(false);

    // Sync input states with formData
    useEffect(() => {
        setContainerInput(formData?.containerTypeLabel || '');
        setCommodityInput(formData?.commodityLabel || '');
    }, [formData?.containerTypeLabel, formData?.commodityLabel]);

    const handleOpenHSNPopup = (e) => {
        e.stopPropagation();
        setOpenHSNPopup(true);
    };

    const handleHSNSelect = (selectedItem) => {
        if (selectedItem) {
            const label = selectedItem.name;
            const value = selectedItem.hsncode;
            setIsSelectingCommodity(true);
            setFormData((prev) => ({
                ...prev,
                commodity: value,
                commodityLabel: label
            }));
            setCommodityInput(label);
            setShowCommodityDropdown(false);
            // Reset the flag after a short delay to allow the input to update
            setTimeout(() => setIsSelectingCommodity(false), 100);
        }
    };

    // Fetch container types on load
    useEffect(() => {
        const fetchContainers = async () => {
            try {
                const response = await fetch(ENDPOINTS.SHIPMENT_CONTAINERS);
                const data = await response.json();
                const formattedOptions = data.data.map((item) => ({
                    value: item.container_type,
                    label: `${item.container_size}' ${item.type_full_name}`,
                }));
                setContainersOptions(formattedOptions);
            } catch (error) {
                console.error('Error fetching containers:', error);
            }
        };
        fetchContainers();
    }, []);

    // Commodity search
    const fetchCommodityDropOptions = async (searchTerm) => {
        setCommodityLoading(true);
        try {
            const response = await fetch(`${ENDPOINTS.HSN_CODE_SEARCH}?hs_code=${searchTerm}&is_hs_code=false`);
            const data = await response.json();
            const formattedOptions = data.data.results.map((item) => ({
                value: item.hs_code,
                label: item.hs_code_description,
            }));
            setCommodityDropOptions(formattedOptions);
            setShowCommodityDropdown(true);
        } catch (error) {
            console.error('Error fetching commodity options:', error);
        } finally {
            setCommodityLoading(false);
        }
    };

    const debouncedFetch = useCallback(
        debounce((term) => {
            if (term.length >= 3) fetchCommodityDropOptions(term);
            else setShowCommodityDropdown(false);
        }, 300),
        []
    );

    useEffect(() => {
        if (document.activeElement === document.getElementById('commodity-input') && !isSelectingCommodity) {
            debouncedFetch(commodityInput);
        }
    }, [commodityInput, isSelectingCommodity]);

    const handleSelect = (field, value, label) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
            [`${field}Label`]: label // Store the label in formData
        }));

        // Clear error for the selected field
        if (setErrors) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }

        if (field === 'containerType') {
            setContainerInput(label);
            setShowContainerDropdown(false);
        }
        if (field === 'commodity') {
            setIsSelectingCommodity(true);
            setCommodityInput(label);
            setShowCommodityDropdown(false);
            // Reset the flag after a short delay to allow the input to update
            setTimeout(() => setIsSelectingCommodity(false), 100);
        }
    };
    const router = useRouter();

    return (
        <>
            {/* Commodity */}
            <div className="mb-3 relative">
                <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
                        Commodity <span className="text-red-500">*</span>
                        {commodityLoading && (
                            <svg className="w-4 h-4 ml-2 animate-spin text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                            </svg>
                        )}
                    </label>
                    <button
                        type="button"
                        className="bg-[#fff] text-[#03356E] text-sm border-none underline"
                        onClick={handleOpenHSNPopup}
                    >
                        View HSN Codes
                    </button>
                </div>

                <input
                    id="commodity-input"
                    type="text"
                    value={commodityInput}
                    onChange={(e) => {
                        setCommodityInput(e.target.value);
                        if (setErrors) setErrors(prev => ({ ...prev, commodity: '' }));
                    }}
                    onFocus={() => {
                        if (commodityInput.length >= 3) setShowCommodityDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowCommodityDropdown(false), 200)}
                    placeholder="Search commodity"
                    className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
                />
                {showCommodityDropdown && commodityDropOptions.length > 0 && (
                    <ul className="absolute z-10 bg-white border border-gray-300 rounded w-full max-h-40 overflow-y-auto">
                        {commodityDropOptions.map((item) => (
                            <li
                                key={item.value}
                                className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                                onMouseDown={() => handleSelect('commodity', item.value, item.label)}
                            >
                                {item.label}
                            </li>
                        ))}
                    </ul>
                )}
                {errors.commodity && <p className="text-red-600 text-xs mt-xsp">{errors.commodity}</p>}
            </div>

            <section className={`flex justify-between  ` +
                (router.pathname === '/shipment-calculator/quotes'
                    ? 'flex-wrap'
                    : 'gap-1')} >
                {/* Container Type */}
                <div className={
                    `mb-2 relative items-center justify-between mb-1 ` +
                    (router.pathname === '/shipment-calculator/quotes'
                        ? 'w-full'
                        : 'w-[70%] sm:w-full mb-3')}>
                    <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
                        Container Type <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={containerInput}
                        onChange={(e) => {
                            setContainerInput(e.target.value);
                            setShowContainerDropdown(true);
                            if (setErrors) setErrors(prev => ({ ...prev, containerType: '' }));
                        }}
                        onFocus={() => setShowContainerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowContainerDropdown(false), 200)}
                        placeholder2
                        placeholder="Search container type"
                        className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
                    />
                    {showContainerDropdown && containersOptions.length > 0 && (
                        <ul className="absolute z-10 bg-white border border-gray-300 rounded w-full max-h-40 overflow-y-auto">
                            {containersOptions
                                .filter((c) => c.label.toLowerCase().includes(containerInput.toLowerCase()))
                                .map((item) => (
                                    <li
                                        key={item.value}
                                        className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                                        onMouseDown={() => handleSelect('containerType', item.value, item.label)}
                                    >
                                        {item.label}
                                    </li>
                                ))}
                        </ul>
                    )}
                    {errors.containerType && <p className="text-red-600 text-xs mt-xsp">{errors.containerType}</p>}
                </div>
                {/* Unit */}

                <div className={
                    `mb-2 relative ` +
                    (router.pathname === '/shipment-calculator/quotes'
                        ? 'w-full'
                        : 'w-[30%] sm:w-full mb-3')} >
                    <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
                        Unit <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        min="1"
                        name="unit"
                        value={formData.unit}
                        onChange={handleChange}
                        className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
                        placeholder="Number of units"
                    />



                    {/* <div className="flex items-center">
                                <button
                                    type="button"
                                    className="border border-gray-light rounded-l h-9 w-9 min-w-9 flex justify-center items-center"
                                    onClick={() => setFormData({ ...formData, unit: Math.max(0, formData.unit - 1) })}
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    name="unit"
                                    value={formData.unit}
                                    onChange={handleChange}
                                    className="h-9 w-full text-center border-t border-b border-gray-light focus:outline-none text-sm"
                                />
                                <button
                                    type="button"
                                    className="border border-gray-light rounded-r h-9 w-9 min-w-9 flex justify-center items-center"
                                    onClick={() => setFormData({ ...formData, unit: formData.unit + 1 })}
                                >
                                    +
                                </button>
                            </div> */}





                    {errors.unit && <p className="text-red-600 text-xs mt-xsp">{errors.unit}</p>}
                </div>
            </section>

            {/* Dimensions */}
            <div className={`flex sm:flex-col ` +
                (router.pathname === '/shipment-calculator/quotes'
                    ? 'flex-wrap'
                    : 'gap-1 mb-2')} >
                <div className={
                    `mb-2 relative ` +
                    (router.pathname === '/shipment-calculator/quotes'
                        ? 'w-full'
                        : 'w-[70%] sm:w-full')} >
                    <label className="block text-sm font-medium mb-xsp text-grayText">
                        {/* {dim.charAt(0).toUpperCase() + dim.slice(1)} * */}
                        Measurement (L*W*H per unit)
                    </label>
                    <section className="flex">
                        {['length', 'width', 'height'].map((dim, index) => (
                            <div
                                key={dim}
                                className={`${index === 0 ? 'rounded-l-[4px]' : ''} border-t w-[25%] border-b border-l border-gray-light`}
                            >
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    name={dim}
                                    value={formData[dim]}
                                    onChange={handleChange}
                                    className="h-9 w-full focus:outline-none focus:border-[#9C9C9C] px-1 text-sm"
                                    placeholder={dim}
                                />
                                {errors[dim] && <p className="text-red-600 text-xs mt-xsp">{errors[dim]}</p>}
                            </div>

                        ))}
                        <select
                            name="dimensionUnit"
                            value={formData.dimensionUnit}
                            onChange={handleChange}
                            className="h-10 w-[25%] border border-gray-light focus:outline-none rounded-r-[4px] focus:border-[#9C9C9C] px-1 text-sm bg-[#ffffff]"
                        >
                            <option value="CM">CM</option>
                            <option value="IN">IN</option>
                        </select>
                    </section>

                </div>
                {/* Gross Weight */}
                <div className={
                    `mb-2 relative ` +
                    (router.pathname === '/shipment-calculator/quotes'
                        ? 'w-full'
                        : 'w-[30%] sm:w-full mb-3')} >
                    <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
                        Gross Weight <span className="text-red-500">*</span>
                    </label>
                    <div>
                        <div className="flex w-full">
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                name="grossWeight"
                                value={formData.grossWeight}
                                onChange={handleChange}
                                className="h-10 w-[60%] px-[10px] py-[5px] text-dimgray border border-gray-light rounded-l-[4px] border-r-0 focus:outline-none focus:border-[#9C9C9C] text-sm"
                                placeholder="Gross weight"
                            />
                            <select
                                name="grossWeightUnit"
                                value={formData.grossWeightUnit}
                                onChange={handleChange}
                                className="h-10 w-[40%] px-[10px] py-[5px] text-dimgray border border-gray-light rounded-r-[4px] focus:outline-none bg-[#ffffff] focus:border-[#9C9C9C] text-sm"
                            >
                                <option value="KG">KG</option>
                                <option value="LB">LB</option>
                            </select>
                        </div>
                        {errors.grossWeight && <p className="text-red-600 text-xs mt-xsp">{errors.grossWeight}</p>}
                    </div>


                </div>
            </div>

            {/* Dimension Unit */}
            {/* <div className="mb-3">
                <label className="block font-semibold mb-1 text-sm text-gray-700">Dimension Unit</label>
                <select
                    name="dimensionUnit"
                    value={formData.dimensionUnit}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-2 py-1"
                >
                    <option value="CM">CM</option>
                    <option value="IN">IN</option>
                </select>
            </div> */}

            {/* Gross Weight */}
            {/* <div className="mb-3">
                <label className="block font-semibold mb-1 text-sm text-gray-700">Gross Weight *</label>
                <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    name="grossWeight"
                    value={formData.grossWeight}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-2 py-1"
                    placeholder="Gross weight"
                />
                {errors.grossWeight && <p className="text-red-600 text-xs mt-xsp">{errors.grossWeight}</p>}
            </div> */}

            {/* Gross Weight Unit */}
            {/* <div className="mb-3">
                <label className="block font-semibold mb-1 text-sm text-gray-700">Gross Weight Unit</label>
                <select
                    name="grossWeightUnit"
                    value={formData.grossWeightUnit}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-2 py-1"
                >
                    <option value="KG">KG</option>
                    <option value="LB">LB</option>
                </select>
            </div> */}

            {openHSNPopup && (
                <div onClick={(event) => event.stopPropagation()} className="cursor-default">
                    <div className="fixed bg-[rgba(0,0,0,0.6)] inset-0 left-0 top-0 z-[1040] transition h-[100vh] w-[100vw]"> </div>
                    <HSNPOPUP setOpenHSNPopup={setOpenHSNPopup} onHSNSelect={handleHSNSelect} />
                </div>
            )}
        </>
    );
};

export default StepTwoForm;